import { useState } from 'react';
import { MatchState, Player, MATCH_CONFIG, TeamState } from '@/types';
import ServerSelectionModal from './ServerSelectionModal';
import FirstServingTeamSelection from './FirstServingTeamSelection';
import BestCatcherSelectionModal from './BestCatcherSelectionModal';
import { exportSetData } from '@/lib/export';

interface SetWonBannerProps {
  matchState: MatchState;
  onStartNextSet: (firstServingTeamId: string, firstServerId: string) => Promise<void>;
  onUpdateBestCatcher: (playerIds: string[]) => Promise<void>;
}

export default function SetWonBanner({ matchState, onStartNextSet, onUpdateBestCatcher }: SetWonBannerProps) {
  const [showTeamSelection, setShowTeamSelection] = useState(false);
  const [showServerSelection, setShowServerSelection] = useState(false);
  const [selectedServingTeam, setSelectedServingTeam] = useState<TeamState | null>(null);
  const [showBestCatcher, setShowBestCatcher] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  if (!matchState.teamA || !matchState.teamB || !matchState.currentSet || !matchState.match) {
    return null;
  }

  const setId = matchState.currentSetNumber;
  const lastSet = matchState.sets[matchState.sets.length - 1];
  const setWinnerId = lastSet?.winnerId;

  if (!setWinnerId) return null;

  const winner = setWinnerId === matchState.teamA.id ? matchState.teamA : matchState.teamB;

  const handleStartNextSet = () => {
    setShowTeamSelection(true);
  };

  const handleTeamSelect = (teamId: string) => {
    const team = teamId === matchState.teamA!.id ? matchState.teamA! : matchState.teamB!;
    setSelectedServingTeam(team);
    setShowTeamSelection(false);
    setShowServerSelection(true);
  };

  const handleServerSelect = async (player: Player) => {
    if (!selectedServingTeam) return;
    setIsStarting(true);
    try {
      await onStartNextSet(selectedServingTeam.id, player.id);
      setShowServerSelection(false);
      setSelectedServingTeam(null);
    } catch (err) {
      console.error('Failed to start next set:', err);
    } finally {
      setIsStarting(false);
    }
  };

  const handleBestCatcherSave = async (playerIds: string[]) => {
    try {
      await onUpdateBestCatcher(playerIds);
      setShowBestCatcher(false);
    } catch (err) {
      console.error('Failed to save Best Catcher:', err);
    }
  };

  const handleExportSet = async () => {
    setIsExporting(true);
    try {
      await exportSetData(matchState, matchState.currentSetNumber);
    } catch (err) {
      console.error('Failed to export set:', err);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      {/* Banner Overlay */}
      <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
        <div className="bg-surface border border-border rounded-2xl p-6 max-w-md w-full animate-fade-in">
          <h2 className="text-xl font-bold text-text-primary text-center mb-4">
            SET {setId} COMPLETE
          </h2>

          <div
            className="rounded-lg p-4 mb-4"
            style={{ backgroundColor: `${winner.color}15`, borderColor: winner.color, borderWidth: 2 }}
          >
            <p
              className="text-2xl font-bold text-center"
              style={{ color: winner.color }}
            >
              {winner.name.toUpperCase()} WIN SET {setId}
            </p>
          </div>

          {/* Final Score */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="text-center">
              <p className="text-xs font-semibold uppercase mb-1" style={{ color: matchState.teamA?.color }}>
                {matchState.teamA?.shortName}
              </p>
              <p className="font-mono-nums text-4xl font-bold text-text-primary">
                {lastSet.scoreA}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs font-semibold uppercase mb-1" style={{ color: matchState.teamB?.color }}>
                {matchState.teamB?.shortName}
              </p>
              <p className="font-mono-nums text-4xl font-bold text-text-primary">
                {lastSet.scoreB}
              </p>
            </div>
          </div>

          {/* Sets Won */}
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold" style={{ color: matchState.teamA?.color }}>
                {matchState.teamA?.shortName}
              </p>
              <div className="flex gap-1">
                {Array.from({ length: MATCH_CONFIG[matchState.match.matchType].maxSets }).map((_, i) => (
                  <span
                    key={i}
                    className={`w-3 h-3 rounded-full ${
                      i < matchState.teamA!.setsWon ? 'bg-team-a' : 'bg-border'
                    }`}
                  />
                ))}
              </div>
            </div>
            <span className="text-text-muted">vs</span>
            <div className="flex items-center gap-2">
              <div className="flex gap-1">
                {Array.from({ length: MATCH_CONFIG[matchState.match.matchType].maxSets }).map((_, i) => (
                  <span
                    key={i}
                    className={`w-3 h-3 rounded-full ${
                      i < matchState.teamB!.setsWon ? 'bg-team-b' : 'bg-border'
                    }`}
                  />
                ))}
              </div>
              <p className="text-sm font-semibold" style={{ color: matchState.teamB?.color }}>
                {matchState.teamB?.shortName}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3 mb-4">
            <button
              onClick={handleStartNextSet}
              disabled={isStarting}
              className="btn-primary bg-team-a text-white font-bold py-4"
            >
              {isStarting ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  STARTING...
                </span>
              ) : (
                'START SET ' + (setId + 1)
              )}
            </button>

            <button
              onClick={() => setShowBestCatcher(true)}
              className="btn-secondary w-full"
            >
              BEST CATCHER
            </button>

            <button
              onClick={handleExportSet}
              disabled={isExporting}
              className="btn-secondary w-full"
            >
              {isExporting ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-text-secondary border-t-transparent rounded-full animate-spin" />
                  EXPORTING...
                </span>
              ) : (
                'EXPORT SET DATA'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* First Serving Team Selection Modal */}
      {showTeamSelection && matchState.teamA && matchState.teamB && (
        <FirstServingTeamSelection
          teamA={matchState.teamA}
          teamB={matchState.teamB}
          setNumber={setId + 1}
          onSelect={handleTeamSelect}
          onCancel={() => setShowTeamSelection(false)}
        />
      )}

      {/* Server Selection Modal */}
      {showServerSelection && selectedServingTeam && (
        <ServerSelectionModal
          team={selectedServingTeam}
          eligibleServers={{ eligible: selectedServingTeam.players, ineligible: [] }}
          onSelect={handleServerSelect}
          onCancel={() => { setShowServerSelection(false); setSelectedServingTeam(null); }}
          reason="first-server"
        />
      )}

      {/* Best Catcher Selection Modal */}
      {showBestCatcher && (
        <BestCatcherSelectionModal
          teamAName={matchState.teamA.name}
          teamBName={matchState.teamB.name}
          teamAColor={matchState.teamA.color}
          teamBColor={matchState.teamB.color}
          playersTeamA={matchState.teamA.players}
          playersTeamB={matchState.teamB.players}
          initiallySelectedIds={matchState.bestCatcherIds}
          onSave={handleBestCatcherSave}
          onCancel={() => setShowBestCatcher(false)}
          setNumber={setId}
        />
      )}
    </>
  );
}
